import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["message"];
import { sendMessageRpc, sendMessagesRpc, sendToolResponseRpc, resumeStreamRpc, cancelTurnRpc, submitFeedbackRpc, sendToolApprovalRpc } from '../../../__generated__/chirp/com/hubspot/breeze/engine/rpc/BreezeEngineStreamingService';
import { mapBreezeRpcError } from '../utils/chatHelpers';
import { createDefaultChirpClient } from '../chirpClient';
let defaultStreamingClient = null;
function getDefaultStreamingClient() {
  if (!defaultStreamingClient) {
    defaultStreamingClient = createDefaultChirpClient();
  }
  return defaultStreamingClient;
}
function setupStreamState(handler, signal) {
  let resolve = null;
  const state = {
    buffer: [],
    done: false,
    error: null,
    wake() {
      if (resolve) {
        resolve();
        resolve = null;
      }
    },
    waitForChange() {
      return new Promise(r => {
        resolve = r;
      });
    }
  };
  handler.onMessage(msg => {
    if (signal.aborted) return;
    if (msg.type === 'data') {
      state.buffer.push(msg.data);
    } else if (msg.type === 'rpcError') {
      state.error = mapBreezeRpcError(msg.rpcError);
    }
    state.wake();
  });
  handler.then(() => {
    state.done = true;
    state.wake();
  }, error => {
    if (!state.error) {
      state.error = error != null && typeof error === 'object' && 'errorType' in error ? mapBreezeRpcError(error) : {
        type: 'TRANSPORT_ERROR',
        message: error instanceof Error ? error.message : 'Stream transport error',
        cause: error
      };
    }
    state.done = true;
    state.wake();
  }).catch(() => {});
  return state;
}
async function* drainStream(state, signal) {
  while (true) {
    if (signal.aborted) return;
    if (state.error) throw state.error;
    while (state.buffer.length > 0) {
      yield state.buffer.shift();
      if (signal.aborted) return;
      if (state.error) throw state.error;
    }
    if (state.done) return;
    await state.waitForChange();
  }
}
function streamToAsyncIterable(handler, signal) {
  const state = setupStreamState(handler, signal);
  return drainStream(state, signal);
}
export function createStreamingBreezeConnection(chirpClient) {
  const client = chirpClient !== null && chirpClient !== void 0 ? chirpClient : getDefaultStreamingClient();
  let activeHandler = null;
  function startStream(rpc, request, signal) {
    if (activeHandler) {
      activeHandler.abort();
      activeHandler = null;
    }
    const handler = client.stream(rpc, {
      request
    });
    activeHandler = handler;
    const onAbort = () => {
      handler.abort();
      activeHandler = null;
    };
    signal.addEventListener('abort', onAbort, {
      once: true
    });
    handler.then(() => {
      signal.removeEventListener('abort', onAbort);
      activeHandler = null;
    }, () => {
      signal.removeEventListener('abort', onAbort);
      activeHandler = null;
    }).catch(() => {});
    return streamToAsyncIterable(handler, signal);
  }
  return {
    streamSendMessage(request, signal) {
      // Switch on the union-shaped message field: string → singular RPC,
      // content blocks → plural RPC. This is the only place the two paths
      // diverge.
      const {
          message
        } = request,
        common = _objectWithoutPropertiesLoose(request, _excluded);
      if (typeof message === 'string') {
        return startStream(sendMessageRpc, Object.assign({}, common, {
          message
        }), signal);
      }
      return startStream(sendMessagesRpc, Object.assign({}, common, {
        contentBlocks: message
      }), signal);
    },
    streamSendToolResponse(request, signal) {
      return startStream(sendToolResponseRpc, request, signal);
    },
    streamSendToolApproval(request, signal) {
      return startStream(sendToolApprovalRpc, request, signal);
    },
    streamResume(request, signal) {
      return startStream(resumeStreamRpc, request, signal);
    },
    async cancelTurn(request) {
      try {
        await client.call(cancelTurnRpc, {
          request
        });
      } catch (_unused) {
        // TURN_NOT_FOUND / TURN_NOT_CANCELLABLE treated as no-ops
      }
    },
    async submitFeedback(request) {
      await client.call(submitFeedbackRpc, {
        request
      });
    },
    abort() {
      if (activeHandler) {
        activeHandler.abort();
        activeHandler = null;
      }
    }
  };
}